
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'

interface SubmoduleEditDialogProps {
  submodule: any
  isOpen: boolean
  onClose: () => void
  onSave: (submoduleData: any) => Promise<void>
}

const SubmoduleEditDialog = ({ submodule, isOpen, onClose, onSave }: SubmoduleEditDialogProps) => {
  // Debug: Check if supabase client is available
  console.log('SubmoduleEditDialog rendered, supabase client:', supabase)
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    summary: '',
    content_type: 'video',
    video_url: '',
    infographic_url: '',
    content_text: '',
    duration: '',
    order_index: 1,
    video_file_path: '' as string | null,
    video_file_name: '' as string | null,
    video_file_size: 0 as number | null,
  })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    if (submodule) {
      setFormData({
        title: submodule.title || '',
        description: submodule.description || '',
        summary: submodule.summary || '',
        content_type: submodule.content_type || 'video',
        video_url: submodule.video_url || '',
        infographic_url: submodule.infographic_url || '',
        content_text: submodule.content_text || '',
        duration: submodule.duration || '',
        order_index: submodule.order_index || 1,
        video_file_path: submodule.video_file_path || '',
        video_file_name: submodule.video_file_name || '',
        video_file_size: submodule.video_file_size || 0,
      })
    }
  }, [submodule])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Please choose a video file', variant: 'destructive' })
      return
    }
    setSelectedFile(f)
  }

  const handleUploadVideo = async () => {
    if (!selectedFile) return
    
    // Debug: Check supabase client again
    console.log('handleUploadVideo called, supabase client:', supabase)
    if (!supabase) {
      toast({ title: 'Error', description: 'Supabase client not available', variant: 'destructive' })
      return
    }
    
    try {
      setUploading(true)
      console.log('Starting upload for file:', selectedFile.name, 'size:', selectedFile.size)
      
      const ext = selectedFile.name.split('.')?.pop()
      const fileName = `${submodule?.id || 'new'}-${Date.now()}.${ext}`
      // Storage path must be RELATIVE to the bucket; do not prefix with the bucket name
      const path = `${fileName}`
      console.log('Uploading to path:', path)
      
      const { data, error } = await supabase.storage
        .from('module-videos')
        .upload(path, selectedFile, { cacheControl: '3600', upsert: false })
      
      if (error) {
        console.error('Storage upload error:', error)
        throw error
      }
      
      console.log('Storage upload success:', data)

      // For private buckets, we don't use public URLs - only signed URLs
      // The video_url field will be empty, but video_file_path will be used for signed URLs
      setFormData(prev => ({
        ...prev,
        video_url: '', // Empty for private buckets
        video_file_path: path,
        video_file_name: selectedFile.name,
        video_file_size: selectedFile.size,
        content_type: 'video',
      }))
      
      console.log('Form data updated with video info')
      toast({ title: 'Video uploaded', description: 'Linked to this submodule.' })
    } catch (e: any) {
      console.error('Upload error:', e)
      toast({ title: 'Upload failed', description: e.message || 'Try again', variant: 'destructive' })
    } finally {
      setUploading(false)
      setSelectedFile(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      return
    }

    try {
      setSaving(true)
      await onSave({
        ...submodule,
        ...formData
      })
    } catch (error) {
      console.error('Error saving submodule:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      summary: '',
      content_type: 'video',
      video_url: '',
      infographic_url: '',
      content_text: '',
      duration: '',
      order_index: 1,
      video_file_path: null,
      video_file_name: null,
      video_file_size: null,
    })
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {submodule?.id ? 'Edit Submodule' : 'Create New Submodule'}
          </DialogTitle>
          <DialogDescription>
            {submodule?.id ? 'Update the submodule information' : 'Add a new submodule to this learning module'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Opening An Account Like A PRO"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content_type">Content Type</Label>
              <Select 
                value={formData.content_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, content_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="reading">Reading</SelectItem>
                  <SelectItem value="infographic">Infographic</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Input
                  id="duration"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="e.g., 10:30"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order_index">Order</Label>
                <Input
                  id="order_index"
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_index: parseInt(e.target.value) || 1 }))}
                  min="1"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what students will learn"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="summary">Summary</Label>
              <Textarea
                id="summary"
                value={formData.summary}
                onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                placeholder="Brief summary for the module overview"
                rows={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Video Upload</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept="video/*" onChange={handleFileSelect} disabled={uploading} />
                <Button type="button" onClick={handleUploadVideo} disabled={!selectedFile || uploading}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
              {formData.video_file_name && (
                <p className="text-xs text-gray-600 break-all">Linked: {formData.video_file_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration</Label>
              <Input
                id="duration"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                placeholder="e.g., 10:30"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="content_text">Additional Content</Label>
            <Textarea
              id="content_text"
              value={formData.content_text}
              onChange={(e) => setFormData(prev => ({ ...prev, content_text: e.target.value }))}
              placeholder="Additional reading material or notes"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.title.trim()}>
              {saving ? 'Saving...' : submodule?.id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default SubmoduleEditDialog
